import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Pagination from '../common/Pagination';
import { api } from '../../services/api';
import * as XLSX from 'xlsx';
import './AuditLog.css';
import { formatAuditActorLabel } from '../../utils/auditActorDisplay';

const AuditLog = () => {
  const { t } = useTranslation('auditLogs');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    action: '',
    resourceType: '',
    status: '',
    userId: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch audit logs
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', currentPage, itemsPerPage, filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: itemsPerPage.toString(),
          ...(filters.action && { action: filters.action }),
          ...(filters.resourceType && { resource_type: filters.resourceType }),
          ...(filters.status && { status: filters.status }),
          ...(filters.userId && { user_id: filters.userId }),
          ...(filters.startDate && { start_date: filters.startDate }),
          ...(filters.endDate && { end_date: filters.endDate }),
          ...(filters.search && { search: filters.search }),
        });
        const response = await api.get(`/api/audit-logs?${params}`);
        return response;
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        return { logs: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 1 } };
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });

  const logs = data?.logs || [];
  const pagination = data?.pagination || { total: 0, totalPages: 1 };

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['audit-logs-stats'],
    queryFn: async () => {
      try {
        return await api.get('/api/audit-logs/stats');
      } catch (err) {
        console.error('Error fetching audit log stats:', err);
        return { stats: {} };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const stats = statsData?.stats || {};

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleExport = async () => {
    try {
      const response = await api.post('/api/audit-logs/export', filters);
      const exportData = response.logs.map(log => ({
        ID: log.id,
        'User ID': log.user_id != null ? log.user_id : 'N/A',
        'User Name': log.actor_display_name || '',
        'User Email': log.user_email || 'N/A',
        'User Role': log.user_role || 'N/A',
        Action: log.action,
        'Resource Type': log.resource_type,
        'Resource ID': log.resource_id || 'N/A',
        Description: log.description || '',
        Status: log.status,
        'Created At': new Date(log.created_at).toLocaleString()
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `audit_logs_${timestamp}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      alert(t('export.success', { count: exportData.length, filename }));
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      alert(t('export.error'));
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('create')) return 'add_circle';
    if (action.includes('update') || action.includes('edit')) return 'edit';
    if (action.includes('delete')) return 'delete';
    if (action.includes('view')) return 'visibility';
    return 'info';
  };

  const getStatusColor = (status) => {
    return status === 'success' ? 'success' : 'error';
  };

  console.log('AuditLog render - data:', { logs: logs.length, isLoading, error: error?.message });
  
  return (
    <div className="audit-log-container">
      <div className="audit-log-header">
        <div>
          <h2>{t('title')}</h2>
          <p>{t('subtitle')}</p>
        </div>
        <div className="audit-log-actions">
          <button 
            className="btn-filter"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="material-icons">filter_list</i>
            {t('filters')}
          </button>
          <button className="btn-export" onClick={handleExport}>
            <i className="material-icons">download</i>
            {t('exportButton')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="audit-log-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total_actions || 0}</div>
            <div className="stat-label">{t('stats.totalActions')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.unique_users || 0}</div>
            <div className="stat-label">{t('stats.uniqueUsers')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.error_count || 0}</div>
            <div className="stat-label">{t('stats.errors')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.delete_actions || 0}</div>
            <div className="stat-label">{t('stats.deleteActions')}</div>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="audit-log-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>{t('filtersPanel.search')}</label>
              <input
                type="text"
                placeholder={t('filtersPanel.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>{t('filtersPanel.action')}</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">{t('filtersPanel.allActions')}</option>
                <option value="user.create">{t('filtersPanel.userCreate')}</option>
                <option value="user.update">{t('filtersPanel.userUpdate')}</option>
                <option value="user.delete">{t('filtersPanel.userDelete')}</option>
                <option value="course.create">{t('filtersPanel.courseCreate')}</option>
                <option value="course.update">{t('filtersPanel.courseUpdate')}</option>
                <option value="course.delete">{t('filtersPanel.courseDelete')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>{t('filtersPanel.resourceType')}</label>
              <select
                value={filters.resourceType}
                onChange={(e) => handleFilterChange('resourceType', e.target.value)}
              >
                <option value="">{t('filtersPanel.allTypes')}</option>
                <option value="user">{t('filtersPanel.user')}</option>
                <option value="course">{t('filtersPanel.course')}</option>
                <option value="content">{t('filtersPanel.content')}</option>
                <option value="payment">{t('filtersPanel.payment')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>{t('filtersPanel.status')}</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">{t('filtersPanel.allStatus')}</option>
                <option value="success">{t('filtersPanel.success')}</option>
                <option value="error">{t('filtersPanel.error')}</option>
              </select>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>{t('filtersPanel.startDate')}</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>{t('filtersPanel.endDate')}</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>{t('filtersPanel.userId')}</label>
              <input
                type="number"
                placeholder={t('filtersPanel.userIdPlaceholder')}
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <button 
                className="btn-clear-filters"
                onClick={() => setFilters({
                  action: '',
                  resourceType: '',
                  status: '',
                  userId: '',
                  startDate: '',
                  endDate: '',
                  search: ''
                })}
              >
                {t('filtersPanel.clearFilters')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="audit-log-table-container">
        {isLoading ? (
          <div className="loading-state">
            <i className="material-icons spinning">hourglass_empty</i>
            {t('states.loading')}
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="material-icons">error</i>
            {t('states.error')}: {error.message}
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <i className="material-icons">description</i>
            <p>{t('states.empty.title')}</p>
            <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>
              {t('states.empty.description')}
            </p>
          </div>
        ) : (
          <>
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>{t('table.headers.id')}</th>
                  <th>{t('table.headers.timestamp')}</th>
                  <th>{t('table.headers.user')}</th>
                  <th>{t('table.headers.action')}</th>
                  <th>{t('table.headers.resource')}</th>
                  <th>{t('table.headers.description')}</th>
                  <th>{t('table.headers.status')}</th>
                  <th>{t('table.headers.ipAddress')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{new Date(log.created_at).toLocaleString()}</td>
                    <td>
                      <div className="user-info">
                        <div className="user-email">{formatAuditActorLabel(log)}</div>
                        <div className="user-role">{log.user_role || t('table.notAvailable')}</div>
                      </div>
                    </td>
                    <td>
                      <div className="action-cell">
                        <i className="material-icons">{getActionIcon(log.action)}</i>
                        <span>{log.action}</span>
                      </div>
                    </td>
                    <td>
                      <div className="resource-cell">
                        <span className="resource-type">{log.resource_type}</span>
                        {log.resource_id && (
                          <span className="resource-id">#{log.resource_id}</span>
                        )}
                      </div>
                    </td>
                    <td className="description-cell">{log.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td>{log.ip_address || t('table.notAvailable')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newLimit) => {
                  setItemsPerPage(newLimit);
                  setCurrentPage(1);
                }}
                showItemsPerPage={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLog;



